'use client';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';
import { forgeFoodService } from '@/lib/services/forgeFoodService';
import { forgeGoalsService } from '@/lib/services/forgeGoalsService';
import { createClient } from '@/lib/supabase/client';

interface AIFoodResult {
  name: string;
  servingSize: number;
  servingUnit: string;
  cal100: number;
  protein100: number;
  carbs100: number;
  fat100: number;
  fibre100: number;
  calPerServing: number;
  proteinPerServing: number;
  carbsPerServing: number;
  fatPerServing: number;
  fibrePerServing: number;
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
}

interface AIQuickSearchProps {
  onAddFood?: (food: {
    name: string;
    grams: number;
    cal: number;
    protein: number;
    carbs: number;
    fat: number;
  }, mealId: string) => void;
  meals: { id: string; name: string }[];
}

const SYSTEM_PROMPT = `You are a precise nutritional database assistant. When given a food name, return ONLY a valid JSON object with nutritional data. Be accurate — use real-world nutritional values from food databases. For branded products (like "Bar One chocolate"), use the actual product's nutritional values.

Return this exact JSON structure:
{
  "name": "exact food name",
  "servingSize": number (typical serving in grams),
  "servingUnit": "string (e.g. bar, slice, cup, piece)",
  "cal100": number (calories per 100g),
  "protein100": number (protein grams per 100g),
  "carbs100": number (carbs grams per 100g),
  "fat100": number (fat grams per 100g),
  "fibre100": number (fibre grams per 100g),
  "calPerServing": number,
  "proteinPerServing": number,
  "carbsPerServing": number,
  "fatPerServing": number,
  "fibrePerServing": number,
  "confidence": "high" | "medium" | "low",
  "notes": "optional note about the food or data source"
}

Return ONLY the JSON object. No explanation, no markdown, no extra text.`;

function parseAIResponse(raw: string): AIFoodResult | null {
  try {
    const cleaned = raw.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const parsed = JSON.parse(cleaned);
    if (
      typeof parsed.name === 'string' &&
      typeof parsed.cal100 === 'number' &&
      typeof parsed.protein100 === 'number'
    ) {
      return parsed as AIFoodResult;
    }
    return null;
  } catch {
    return null;
  }
}

function confidenceBadge(confidence: AIFoodResult['confidence']) {
  if (confidence === 'high') return <span className="text-2xs px-1.5 py-0.5 rounded bg-green-500/15 text-green-400 font-bold">HIGH CONFIDENCE</span>;
  if (confidence === 'medium') return <span className="text-2xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-bold">MEDIUM CONFIDENCE</span>;
  return <span className="text-2xs px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-bold">LOW CONFIDENCE</span>;
}

export default function AIQuickSearch({ onAddFood, meals }: AIQuickSearchProps) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<AIFoodResult | null>(null);
  const [grams, setGrams] = useState(100);
  const [useServing, setUseServing] = useState(false);
  const [selectedMealId, setSelectedMealId] = useState<string>('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedFoodId, setSavedFoodId] = useState<string | null>(null);
  const [isSavingFav, setIsSavingFav] = useState(false);
  const [savedAsFav, setSavedAsFav] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o-mini', false);

  useEffect(() => {
    if (error) toast.error('AI search failed. Please try again.');
  }, [error]);

  useEffect(() => {
    if (meals.length > 0 && !selectedMealId) {
      setSelectedMealId(meals[0].id);
    }
  }, [meals, selectedMealId]);

  useEffect(() => {
    if (response && hasSearched) {
      const parsed = parseAIResponse(response);
      if (parsed) {
        setResult(parsed);
        setGrams(parsed.servingSize || 100);
        setUseServing(true);
        setSavedFoodId(null);
      } else {
        toast.error('Could not parse nutritional data. Try a more specific food name.');
      }
    }
  }, [response, hasSearched]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim() || isLoading) return;
    setResult(null);
    setHasSearched(true);
    setSavedFoodId(null);
    sendMessage([
      { role: 'system', content: SYSTEM_PROMPT },
      { role: 'user', content: `Get nutritional data for: ${query.trim()}` },
    ], { max_completion_tokens: 500 });
  }

  function calcNutrient(per100: number, g: number) {
    return Math.round((per100 * g) / 100);
  }

  const displayGrams = useServing && result ? result.servingSize : grams;
  const displayCal = result ? calcNutrient(result.cal100, displayGrams) : 0;
  const displayProtein = result ? calcNutrient(result.protein100, displayGrams) : 0;
  const displayCarbs = result ? calcNutrient(result.carbs100, displayGrams) : 0;
  const displayFat = result ? calcNutrient(result.fat100, displayGrams) : 0;
  const displayFibre = result ? calcNutrient(result.fibre100, displayGrams) : 0;

  async function handleSaveToDatabase() {
    if (!result || isSaving) return;
    setIsSaving(true);
    try {
      const saved = await forgeFoodService.saveFood({
        name: result.name,
        servingSizeG: result.servingSize,
        servingUnit: result.servingUnit,
        calPer100g: result.cal100,
        proteinPer100g: result.protein100,
        carbsPer100g: result.carbs100,
        fatPer100g: result.fat100,
        fibrePer100g: result.fibre100,
        calPerServing: result.calPerServing,
        proteinPerServing: result.proteinPerServing,
        carbsPerServing: result.carbsPerServing,
        fatPerServing: result.fatPerServing,
        fibrePerServing: result.fibrePerServing,
        confidence: result.confidence,
        notes: result.notes,
      });
      if (saved) {
        setSavedFoodId(saved.id);
        setSavedAsFav(false);
        toast.success(`${result.name} saved to Hunter's List!`);
      }
    } catch (err: any) {
      toast.error('Could not save to database. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveToFavourites() {
    if (!result || !savedFoodId || isSavingFav) return;
    setIsSavingFav(true);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const fav = await forgeGoalsService.addFavouriteFood(user.id, {
        foodId: savedFoodId,
        foodName: result.name,
        calPerServing: result.calPerServing,
        proteinPerServing: result.proteinPerServing,
        carbsPerServing: result.carbsPerServing,
        fatPerServing: result.fatPerServing,
        servingSizeG: result.servingSize,
        servingUnit: result.servingUnit,
      });
      if (fav) {
        setSavedAsFav(true);
        toast.success(`${result.name} added to favourites ⭐`);
      }
    } catch {
      toast.error('Could not save to favourites.');
    } finally {
      setIsSavingFav(false);
    }
  }

  async function handleAdd() {
    if (!result || !selectedMealId || !onAddFood) return;
    onAddFood(
      {
        name: result.name,
        grams: displayGrams,
        cal: displayCal,
        protein: displayProtein,
        carbs: displayCarbs,
        fat: displayFat,
      },
      selectedMealId
    );
    // Also log to Supabase
    try {
      await forgeFoodService.logFood({
        foodId: savedFoodId || undefined,
        foodName: result.name,
        mealType: selectedMealId,
        grams: displayGrams,
        cal: displayCal,
        proteinG: displayProtein,
        carbsG: displayCarbs,
        fatG: displayFat,
        fibreG: displayFibre,
      });
    } catch {
      // Non-blocking — UI already updated
    }
    toast.success(`${result.name} added to ${meals.find(m => m.id === selectedMealId)?.name || 'meal'}`);
    setQuery('');
    setResult(null);
    setHasSearched(false);
    setGrams(100);
    setUseServing(false);
    setSavedFoodId(null);
    setSavedAsFav(false);
    inputRef.current?.focus();
  }

  return (
    <div className="card-forge rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-border/50">
        <div className="w-6 h-6 rounded-md bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Icon name="SparklesIcon" size={13} className="text-primary" />
        </div>
        <div>
          <div className="text-forge-label leading-none">AI Quick Search</div>
          <div className="text-2xs text-muted-foreground mt-0.5">Search any food — AI returns exact macros</div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Search input */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. Bar One chocolate, Big Mac..."
              className="w-full bg-input border border-border rounded-md pl-9 pr-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-colors"
              disabled={isLoading}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="btn-forge-primary px-4 py-2.5 rounded-md text-sm font-bold flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Searching...</span>
              </>
            ) : (
              <>
                <Icon name="SparklesIcon" size={13} />
                <span>Search</span>
              </>
            )}
          </button>
        </form>

        {/* Loading state */}
        {isLoading && (
          <div className="rounded-lg bg-card-elevated border border-border/50 p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <svg className="animate-spin w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>AI is looking up nutritional data...</span>
            </div>
          </div>
        )}

        {/* Result card */}
        {result && !isLoading && (
          <div className="rounded-lg bg-card-elevated border border-border/60 overflow-hidden">
            {/* Food name + confidence */}
            <div className="px-4 py-3 border-b border-border/50 flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-sm text-foreground">{result.name}</div>
                {result.notes && (
                  <div className="text-2xs text-muted-foreground mt-0.5">{result.notes}</div>
                )}
              </div>
              {confidenceBadge(result.confidence)}
            </div>

            {/* Serving toggle */}
            <div className="px-4 py-3 border-b border-border/50">
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={() => setUseServing(false)}
                  className={`text-xs px-3 py-1.5 rounded-md font-bold transition-colors ${!useServing ? 'bg-primary text-primary-foreground' : 'bg-input text-muted-foreground hover:text-foreground'}`}
                >
                  Custom (g)
                </button>
                <button
                  onClick={() => { setUseServing(true); setGrams(result.servingSize); }}
                  className={`text-xs px-3 py-1.5 rounded-md font-bold transition-colors ${useServing ? 'bg-primary text-primary-foreground' : 'bg-input text-muted-foreground hover:text-foreground'}`}
                >
                  Per Serving ({result.servingSize}{result.servingUnit !== 'g' ? ` ${result.servingUnit}` : 'g'})
                </button>
              </div>
              {!useServing && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={grams}
                    onChange={(e) => setGrams(Math.max(1, Number(e.target.value)))}
                    className="w-20 bg-input border border-border rounded px-2 py-1.5 text-sm text-foreground font-tabular focus:outline-none focus:border-primary/50 text-center"
                    min={1}
                  />
                  <span className="text-sm text-muted-foreground">grams</span>
                </div>
              )}
            </div>

            {/* Macro grid */}
            <div className="grid grid-cols-5 divide-x divide-border/50 border-b border-border/50">
              {[
                { label: 'KCAL', value: displayCal, color: 'text-foreground', bold: true },
                { label: 'PROTEIN', value: `${displayProtein}g`, color: 'text-primary' },
                { label: 'CARBS', value: `${displayCarbs}g`, color: 'text-accent' },
                { label: 'FAT', value: `${displayFat}g`, color: 'text-muted-foreground' },
                { label: 'FIBRE', value: `${displayFibre}g`, color: 'text-muted-foreground' },
              ].map((macro) => (
                <div key={macro.label} className="flex flex-col items-center py-3 px-1">
                  <div className={`font-tabular font-black text-sm ${macro.color}`}>{macro.value}</div>
                  <div className="text-2xs text-muted-foreground mt-0.5 tracking-wider">{macro.label}</div>
                </div>
              ))}
            </div>

            {/* Per 100g reference */}
            <div className="px-4 py-2 border-b border-border/50 bg-background/30">
              <div className="text-2xs text-muted-foreground font-tabular">
                Per 100g: {result.cal100} kcal · P {result.protein100}g · C {result.carbs100}g · F {result.fat100}g · Fibre {result.fibre100}g
              </div>
            </div>

            {/* Save to database button */}
            <div className="px-4 pt-3 pb-1 space-y-2">
              <button
                onClick={handleSaveToDatabase}
                disabled={isSaving || !!savedFoodId}
                className={`w-full py-2 rounded-md text-xs font-bold tracking-wider flex items-center justify-center gap-2 transition-all ${
                  savedFoodId
                    ? 'bg-green-500/15 text-green-400 border border-green-500/30 cursor-default' :'border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>SAVING...</span>
                  </>
                ) : savedFoodId ? (
                  <>
                    <Icon name="CheckIcon" size={13} />
                    <span>SAVED TO HUNTER&apos;S LIST ✓</span>
                  </>
                ) : (
                  <>
                    <Icon name="BookmarkIcon" size={13} />
                    <span>SAVE TO HUNTER&apos;S LIST</span>
                  </>
                )}
              </button>
              {savedFoodId && (
                <button
                  onClick={handleSaveToFavourites}
                  disabled={isSavingFav || savedAsFav}
                  className={`w-full py-2 rounded-md text-xs font-bold tracking-wider flex items-center justify-center gap-2 transition-all ${
                    savedAsFav
                      ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30 cursor-default' :'border border-dashed border-amber-500/30 text-amber-400/70 hover:border-amber-400/60 hover:text-amber-400 disabled:opacity-50'
                  }`}
                >
                  {isSavingFav ? (
                    <span>SAVING...</span>
                  ) : savedAsFav ? (
                    <><Icon name="StarIcon" size={13} /><span>ADDED TO FAVOURITES ⭐</span></>
                  ) : (
                    <><Icon name="StarIcon" size={13} /><span>ADD TO FAVOURITES</span></>
                  )}
                </button>
              )}
            </div>

            {/* Add to meal */}
            <div className="px-4 py-3 flex items-center gap-3">
              <select
                value={selectedMealId}
                onChange={(e) => setSelectedMealId(e.target.value)}
                className="flex-1 bg-input border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
              >
                {meals.map((meal) => (
                  <option key={meal.id} value={meal.id}>{meal.name}</option>
                ))}
              </select>
              <button
                onClick={handleAdd}
                className="btn-forge-primary px-5 py-2 rounded-md text-sm font-black tracking-wider flex items-center gap-2"
              >
                <Icon name="PlusIcon" size={14} />
                ADD
              </button>
            </div>
          </div>
        )}

        {/* Empty hint */}
        {!result && !isLoading && !hasSearched && (
          <div className="text-center py-3">
            <div className="text-2xs text-muted-foreground">
              Try: &ldquo;Bar One chocolate&rdquo;, &ldquo;Big Mac&rdquo;, &ldquo;Woolworths chicken pie&rdquo;
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
